<script setup lang="ts">
import type { DataTableHeaders } from '@/plugins/vuetify'
import type MDBSemi from '@/types/ferme/semi'
import type MDBVegcoBucket from '@/types/vegco/bucket'
import { roundTo, toDate_wd_dd_MMMM, getSemiSuperficie, getSemiDateRécoltePrévue, getSemiVérifiéTextColor } from '@/utils/helpers'
import { unitésParBoîte } from '@/utils/helpers-vegco'

//https://stackoverflow.com/questions/57807226/vuetify-data-table-add-horizontal-stroller-bar

/*definePage({
  meta: {
    id: 'test-tableau',
    title: 'Tableau Test',
    icon: 'M7.73 11.93C7.73 13.65 7.71 13.76 7.5 14C7.31 14.17 7.12 14.23 6.74 14.23L6.23 14.24L6.2 11.97L6.18 9.7H6.7C7.05 9.7 7.3 9.77 7.47 9.91C7.71 10.12 7.73 10.16 7.73 11.93M22 7.5V16.5C22 17.61 21.11 18.5 20 18.5H4C2.89 18.5 2 17.61 2 16.5V7.5C2 6.39 2.89 5.5 4 5.5H20C21.11 5.5 22 6.39 22 7.5M8.93 11.73C8.9 9.89 8.88 9.74 8.64 9.34C8.24 8.66 7.79 8.5 6.28 8.5H5V15.5H6.21C7.54 15.5 8.1 15.33 8.5 14.79C8.91 14.26 9 13.81 8.93 11.73M13.12 8.5H11.64C10.15 8.5 10.14 8.5 9.93 8.78S9.7 9.21 9.7 12V14.96L9.97 15.23C10.22 15.5 10.28 15.5 11.68 15.5H13.12V14.31L12.03 14.27L10.93 14.24V12.6L11.61 12.57L12.27 12.53V11.34H10.88V9.7H13.12V8.5M19 8.56C19 8.5 18.7 8.5 18.34 8.5L17.66 8.56L17.07 10.91C16.69 12.39 16.45 13.18 16.4 13.04C16.32 12.77 15.26 8.6 15.26 8.55C15.26 8.5 14.95 8.5 14.58 8.5H13.89L14.3 10.05C14.5 10.92 14.89 12.33 15.11 13.2C15.45 14.55 15.57 14.85 15.86 15.14C16.06 15.36 16.31 15.5 16.47 15.5C16.8 15.5 17.23 15.16 17.37 14.77C17.5 14.5 19 8.69 19 8.56Z',
    drawerIndex: 4,
    requiresAuth: true,
    requiresSiteAccess: 'semis'
  },
})*/

const storeU = useUtilisateurStore()
const isLoading = ref(true)
const révision = ref('')

const semis = ref([] as MDBSemi[])
const buckets = ref([] as MDBVegcoBucket[])
const search = ref('')
const headers = ref<DataTableHeaders>([
  { title: '#', key: 'formattedNuméro', width: '120px' },
  { title: 'Date récolte prévue', key: 'dateRécoltePrévue2', width: '120px' },
  { title: 'Date plantation', key: 'formattedDateDu', width: '120px' },
  { title: 'Champ', key: 'champ', width: '60px'},
  { title: 'Variété', key: 'variété', width: '110px'},
  { title: 'Planches', key: 'planches', width: '80px'},
  { title: 'Sup.', key: 'formattedSuperficie', width: '60px'},
  { title: 'Rendement prévu', key: 'rendementPrévuBoîte', width: '80px'},
  { title: 'Nb. boîtes', key: 'nbBoîtes', width: '80px'},
  { title: 'Degré-jour', key: 'dj', sortable: false, width: '160px' },
  { title: 'Commentaires', key: 'cmnts'},
  { title: 'Vérifié', key: 'dateVérification', width: '150px'},
  { title: 'Actions', key: 'actions', sortable: false, width: '120px' }
]);
const dialogColumns = ref(false)
const visibleColumns = ref(headers.value?.map(header => header.key).filter(key =>
  //key !== 'variété' &&
  (key !== 'dj' || storeU.siteAAccès('degréjours'))
) ?? []);
const filteredHeaders = computed(() => headers.value?.filter(header => visibleColumns.value.includes(header.key)) ?? []);
const voirFermés = ref(false)
const showAjustementDJ = computed(() => storeU.siteAAccès('degréjours') &&
  (storeU.cultureSélectionnée.nom == 'Laitue Romaine' || storeU.cultureSélectionnée.nom == 'Iceberg / Pommée' || storeU.cultureSélectionnée.nom == 'Radis Chinois'))
const editAjustementDJ = ref(false)
const ajustementDJModifié = ref(0)
const unitParBoîte = computed(() => unitésParBoîte(storeU.cultureSélectionnée.nom))
const formattedSemis = computed(() => {
  let semisList = semis.value;

  if (!voirFermés.value) {
    semisList = semisList.filter(semi => !semi.fermé)
  }

  return semisList.map(semi => ({
    ...semi,
    formattedNuméro: semi.numéro + (semi.fermé ? ' (*)' : ''),
    formattedDateDu: toDate_wd_dd_MMMM(semi.dateDu),
    dateRécoltePrévue2: getSemiDateRécoltePrévue(semi),
    planches: semi.plancheDe + '-' + semi.plancheÀ + ' (' + (semi.plancheÀ - semi.plancheDe + 1) + ')',
    cmnts: getCommentaires(semi),
    rendementPrévuBoîte: semi.rendementPrévu / unitParBoîte.value,
    nbBoîtes: roundTo(getSemiSuperficie(semi) * semi.rendementPrévu / unitParBoîte.value, 0),
    formattedSuperficie: roundTo(getSemiSuperficie(semi), 2),
    djRéelAccumulé: roundTo(semi.degréJour?.djRéelAccumulé ?? 0, 0),
  }));
});

function onAjustementDJCorbeilClick() {
  editAjustementDJ.value = false
  ajustementDJModifié.value = storeU.cultureSélectionnée.ajustementDJ
}

async function onAjustementDJPencilClick() {
  editAjustementDJ.value = !editAjustementDJ.value

  if (storeU.cultureSélectionnée.ajustementDJ != ajustementDJModifié.value) {
    try {
      const siteCS = storeU.siteSélectionné.lettresDappel
      const config = { params: { culture: storeU.cultureSélectionnée.nom, ajustement: ajustementDJModifié.value } }
      await apiProphet.put(`sites/${siteCS}/degrejours/ajustementdj`, null, config)
      Notify.success('Ajustement DJ modifié')
      storeU.cultureSélectionnée.ajustementDJ = ajustementDJModifié.value
    } catch (e) {
      Notify.error(e)
    }
  }
}

function aDesBucketsAvecDesBalises(semi: MDBSemi) {
  return buckets.value.some(b => b.semiId == semi.id && b.balises.length > 0)
}

function getCommentaires(semi: MDBSemi) {
  let commentaires = ''

  if (semi.commentaires) {
    if (commentaires != '') commentaires += ' ||| '
    commentaires += semi.commentaires
  }

  if (semi.commentairesFarmit) {
    if (commentaires != '') commentaires += ' ||| '
    commentaires += semi.commentairesFarmit
  }

  for (const bucket of buckets.value.filter(b => b.semiId == semi.id)) {
    if (bucket.commentaires != undefined && bucket.commentaires != '') {
      if (commentaires != '') commentaires += ' ||| '
      commentaires += 'Bucket: ' + bucket.commentaires
    }
  }

  return commentaires
}

const totalSuperficie = computed(() => {
  return roundTo(semis.value.reduce((acc, semi) => acc + getSemiSuperficie(semi), 0), 2);
});

const dialogCulture = useTemplateRef('dialogCulture')
async function showDialogCulture() {
  const result = await dialogCulture.value?.open()
  if (result != undefined && result != '') {
    storeU.sélectionnerCulture(result, null)
    ajustementDJModifié.value = storeU.cultureSélectionnée.ajustementDJ
    await getSemis()
  } else {
    isLoading.value = false
  }
}

watch(() => storeU.siteSélectionné, async () => { await getSemis() })
watch(() => storeU.annéeSélectionnée, async () => { await getSemis() })

// Revision check
const intervalId = setInterval(async () => {
  const siteCS = storeU.siteSélectionné.lettresDappel
  if (storeU.cultureSélectionnée.nom == undefined || siteCS == undefined || siteCS == '') {
    return
  }
  const result = await apiProphet.get(`sites/${siteCS}/revisions/semis`)
  if (révision.value != result.data) {
    getSemis()
  }
}, 3000)

async function getSemis() {
  isLoading.value = true
  semis.value = []

  if (storeU.cultureSélectionnée.nom == undefined) {
    Notify.info('Veuillez sélectionner une culture avant de continuer.')
    isLoading.value = false
    return
  }

  try {
    ajustementDJModifié.value = storeU.cultureSélectionnée.ajustementDJ

    const siteCS = storeU.siteSélectionné.lettresDappel

    const result = await apiProphet.get(`sites/${siteCS}/revisions/semis`)
    révision.value = result.data

    const config = { params: { culture: storeU.cultureSélectionnée.nom, année: storeU.annéeSélectionnée } }
    const result2 = await apiProphet.get(`sites/${siteCS}/semis`, config)
    semis.value = result2.data

    const semiIds = semis.value.map((semi) => semi.id)
    const result3 = await apiProphet.post(`vgco/sites/${siteCS}/semis/multiple/buckets/commentaires`, semiIds)
    buckets.value = result3.data
  }
  catch (e) {
    Notify.error(e)
  }
  finally {
    isLoading.value = false
  }
}

const dialogSemi = useTemplateRef('dialogSemi')
async function _newSemi() {
  await dialogSemi.value?.openSemiNew()
  getSemis()
}
async function editSemi(semi: MDBSemi) {
  const modifs = await dialogSemi.value?.openSemiEdit(semi)
  if (modifs)
    getSemis()
}

/*const dialogDelete = useTemplateRef('dialogDelete')
async function deleteSemi(semi: MDBSemi) {
  if (semi.farmitId != undefined && semi.farmitId != '') {
    Notify.error('Impossible de supprimer un semi provenant de Farmit')
    return
  }

  const result = await dialogDelete.value?.open('Êtes-vous certain de vouloir supprimer ce semi ?');
  if (result?.valueOf()) {
    try {
      const siteCS = storeU.siteSélectionné.lettresDappel
      await apiProphet.delete(`sites/${siteCS}/semis/${semi.id}`)
      Notify.success('Supprimé')
      await getSemis()
    } catch (e) {
      Notify.error(e)
    }
  }
}*/

const dialogBuckets = useTemplateRef('dialogBuckets')
async function openBuckets(semi: MDBSemi) {
  const modifs = await dialogBuckets.value?.openBucketsAsync(semi, null)
  if (modifs) {
    getSemis()
  }
}

function getVérifiéColumnValue(dateVérification: string) {
  if (dateVérification == undefined || dateVérification == null || dateVérification == '')
    return 'Jamais'
  else if (new Date(dateVérification).getFullYear() > 2000)
    return toDate_wd_dd_MMMM(dateVérification)
  else
    return 'Jamais'
}

onMounted(async () => {
  if (storeU.cultureSélectionnée.id == undefined) {
    showDialogCulture()
  } else {
    await getSemis()
  }
})

onUnmounted(() => {
  if (intervalId)
    clearInterval(intervalId)
})
</script>

<template>
  <v-card class="mx-4 my-4">
    <v-toolbar extended :extension-height="$vuetify.display.smAndDown ? 128 : 64">
      <v-toolbar-title class="text-h5">
        Semis <YearSelector/>
      </v-toolbar-title>
      <template #append>
        <span v-if="showAjustementDJ" class="mr-10 mt-4">
          <v-text-field
              v-if="!editAjustementDJ" v-model="ajustementDJModifié" label="Ajustement DJ" type="number"
              readonly density="compact" variant="outlined"
              append-icon="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" @click:append="onAjustementDJPencilClick" />
            <v-text-field
              v-else v-model="ajustementDJModifié" label="Ajustement DJ" type="number"
                append-inner-icon="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" append-icon="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M9,8H11V17H9V8M13,8H15V17H13V8Z"
              @click:append="onAjustementDJCorbeilClick" @click:append-inner="onAjustementDJPencilClick"/>
        </span>
        <span class="text-h6 ps-2">
          {{ storeU.cultureSélectionnée.nom }}
          <v-btn color="primary" class="mr-2 ml-2" size="40px" elevation="4" icon="M21,9L17,5V8H10V10H17V13M7,11L3,15L7,19V16H14V14H7V11Z" @click="showDialogCulture"/>
        </span>
      </template>
      <template v-if="$vuetify.display.mdAndUp" #extension>
        <span class="text-subtitle-1 pl-6 pr-6">
          Total sup. : {{ totalSuperficie }}ac
        </span>
        <span class="d-flex align-center pl-6">
          <v-checkbox v-model="voirFermés" label="Fermés (*)" class="ma-0 pa-0" hide-details density="compact" />
        </span>
        <v-spacer/>
        <v-text-field
          v-model="search" prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" label="Recherche"
          density="compact" class="mr-4" rounded="xl" variant="solo"
          single-line hide-details flat
          style="max-width: 250px"
        />
        <!--<v-btn color="primary" class="me-3" elevation="4" icon="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" @click="newSemi"/>-->
        <v-btn color="primary" class="me-3" elevation="4" icon="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" @click="dialogColumns = true"/>
      </template>
      <template v-else #extension>
        <v-container>
          <v-row dense>
            <v-col cols="6" class="text-subtitle-1 pl-6 pr-6">
              Total sup. : {{ totalSuperficie }}ac
            </v-col>
            <v-col cols="6" class="text-subtitle-1">
              <v-checkbox v-model="voirFermés" label="Fermés (*)" class="ma-0 pa-0" hide-details density="compact" />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="search" prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" label="Recherche"
                density="compact" class="mr-4" rounded="xl" variant="solo"
                single-line hide-details flat
              />
            </v-col>
          </v-row>
        </v-container>
      </template>
    </v-toolbar>

    <v-data-table
      :headers="filteredHeaders" :items="formattedSemis" :items-per-page="-1"
      :loading="isLoading" :search="search" item-key="id"
      :sort-by="[{ key: 'numéro', order: 'asc' }]" :sort-desc="false"
      :hide-default-header="$vuetify.display.smAndDown">
      <template #item.dateRécoltePrévue2="{ value }">
        <v-chip :text="value == '' ? 'Aucune date' : toDate_wd_dd_MMMM(value)"/>
      </template>
      <template #item.dateVérification="{ item }">
        <span :class="getSemiVérifiéTextColor(item)">{{ getVérifiéColumnValue(item.dateVérification) }}</span><br/>
        <span v-if="!aDesBucketsAvecDesBalises(item)" class="text-red-lighten-1">Pas de balises</span>
      </template>
      <template v-if="$vuetify.display.smAndDown" #item="props">
        <tr>
          <td class="pt-2 pb-2">
            <v-container>
              <v-row>
                <v-col cols="8">
                  #: {{ props.item.formattedNuméro }}<br/>
                  Date récolte prévue: {{ toDate_wd_dd_MMMM(props.item.dateRécoltePrévue2) }}<br/>
                  Date: {{ props.item.formattedDateDu }}<br/>
                  Champ: {{ props.item.champ }}<br/>
                  Variété: {{ props.item.variété }}<br/>
                  Planches: {{ props.item.planches }}<br/>
                  Superficie: {{ props.item.formattedSuperficie }}<br/>
                  Rendement prévu: {{ props.item.rendementPrévuBoîte }}<br/>
                  Nb boîtes: {{ roundTo(getSemiSuperficie(props.item) * props.item.rendementPrévuBoîte, 0) }}<br/>
                  Commentaires: {{ props.item.cmnts }}<br/>
                  Vérification: <span :class="getSemiVérifiéTextColor(props.item)">{{ getVérifiéColumnValue(props.item.dateVérification) }}</span><br/>
                  <span v-if="!aDesBucketsAvecDesBalises(props.item)" class="text-red-lighten-1">Pas de balises</span>
                </v-col>
                <v-col cols="4">
                  <v-icon class="me-2" size="40" @click="editSemi(props.item)">
                    M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z
                  </v-icon>
                  <v-icon class="me-2" size="40" @click="openBuckets(props.item)">
                    M3 4H21V7H20L17.5 21H6.5L4 7H3V4Z
                  </v-icon>
                </v-col>
              </v-row>
            </v-container>
          </td>
        </tr>
      </template>
      <template #item.dj="{ item }">
        <span v-if="item.degréJour != null" class="mr-2">
          {{roundTo(item.degréJour.djRéelAccumulé, 0)}}
        </span>
        <span v-if="item.degréJour == null">
          <v-icon>M23,12L20.56,9.22L20.9,5.54L17.29,4.72L15.4,1.54L12,3L8.6,1.54L6.71,4.72L3.1,5.53L3.44,9.21L1,12L3.44,14.78L3.1,18.47L6.71,19.29L8.6,22.47L12,21L15.4,22.46L17.29,19.28L20.9,18.46L20.56,14.78L23,12M13,17H11V15H13V17M13,13H11V7H13V13Z</v-icon>
          <v-tooltip activator="parent" location="bottom">
            N'a pas pu créer le système de degré-jour<br/>
            (Pas de date de récolte dans le passé ?)
          </v-tooltip>
        </span>
        <span v-else-if="item.degréJour.complété">
          <v-icon>M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z</v-icon>
          <v-tooltip activator="parent" location="bottom">Récolté</v-tooltip>
        </span>
        <span v-else-if="item.degréJour.djNécessaireCalculé == 0 && item.degréJour.djNécessaireManuel == 0">
          <v-icon>M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z</v-icon>
          <v-tooltip activator="parent" location="bottom">
            N'a pas pu trouvé de degré-jour nécessaire<br/>
            (Pas de données historique de degré-jour pour cette culture ?)
          </v-tooltip>
        </span>
        <span v-else-if="item.degréJour.djNécessaireCalculé != 0">
          ({{ roundTo(item.degréJour.djRéelAccumulé / item.degréJour.djNécessaireCalculé * 100, 0) }}%)
        </span>
        <br/>
        <span v-if="item.degréJour != null" class="mr-2">
          / {{roundTo(item.degréJour.djNécessaireManuel != 0 ? item.degréJour.djNécessaireManuel : item.degréJour.djNécessaireCalculé +
           (item.degréJour.complété ? 0 : ajustementDJModifié), 0) }}
        </span>
      </template>
      <template #item.actions="{ item }">
        <v-icon class="me-2" size="small" @click="editSemi(item)">
          M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z
        </v-icon>
        <v-icon size="small" @click="openBuckets(item)">
          M3 4H21V7H20L17.5 21H6.5L4 7H3V4Z
        </v-icon>
        <!--<v-icon size="small" @click="deleteSemi(item)">
          M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z
        </v-icon>-->
      </template>
    </v-data-table>
  </v-card>

  <DialogCulture ref='dialogCulture'/>
  <DialogConfirm ref="dialogDelete"/>
  <DialogColonnes ref='dialogColonnes' v-model="dialogColumns" v-model:headers="headers" v-model:visibleheaders="visibleColumns" />
  <DialogSemi ref='dialogSemi'/>
  <DialogBuckets ref="dialogBuckets"/>
</template>
